/* global HTMLDivElement, MouseEvent, Node */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";

const DATE_WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function dateToIso(date: Date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseIsoDate(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * Flexible date string parser — accepts many formats users may type (Vietnamese-first).
 * Returns ISO string "YYYY-MM-DD" or null if invalid/out-of-range calendar date.
 *
 * Accepted formats:
 *   "dd/mm/yyyy"  → e.g. "15/06/2026"  (Vietnamese primary)
 *   "dd-mm-yyyy"  → e.g. "15-06-2026"
 *   "dd/mm/yy"   → e.g. "15/06/26" → 2026 (2000+yy)
 *   "dd/m/yyyy"  / "d/mm/yyyy" → single-digit day or month accepted
 *   "yyyy-mm-dd"  → ISO e.g. "2026-06-15"
 */
export function parseTypedDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;

  let year: number | undefined;
  let month: number | undefined;
  let day: number | undefined;

  // ISO: yyyy-mm-dd
  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    year = parseInt(isoMatch[1], 10);
    month = parseInt(isoMatch[2], 10);
    day = parseInt(isoMatch[3], 10);
  }

  // Vietnamese: dd/mm/yyyy, dd-mm-yyyy, dd/mm/yy, d/m/yyyy etc.
  if (year === undefined) {
    const viMatch = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (viMatch) {
      day = parseInt(viMatch[1], 10);
      month = parseInt(viMatch[2], 10);
      const rawYear = parseInt(viMatch[3], 10);
      year = viMatch[3].length === 2 ? 2000 + rawYear : rawYear;
    }
  }

  // Digit-only (mobile numeric keypad — no "/" key): "ddmmyyyy" or "ddmmyy".
  // Only when the whole string is digits, so "15/06/2026" still takes the path above.
  if (year === undefined && /^\d+$/.test(s)) {
    if (s.length === 8) {
      day = parseInt(s.slice(0, 2), 10);
      month = parseInt(s.slice(2, 4), 10);
      year = parseInt(s.slice(4), 10);
    } else if (s.length === 6) {
      day = parseInt(s.slice(0, 2), 10);
      month = parseInt(s.slice(2, 4), 10);
      year = 2000 + parseInt(s.slice(4), 10);
    }
  }

  if (year === undefined || month === undefined || day === undefined) return null;

  // Validate: month 1–12, day 1–31
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Validate real calendar date (rejects 31/02, 31/04, etc.)
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return dateToIso(date);
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function calendarCells(month: Date) {
  const first = startOfMonth(month);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = addDays(first, -mondayOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

/** Format ISO date as vi-VN "dd/mm/yyyy" for display in the text field. */
function formatDateDisplay(isoValue: string): string {
  const date = parseIsoDate(isoValue);
  if (!date) return "";
  return date.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function monthLabel(date: Date) {
  const label = date.toLocaleDateString("vi-VN", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function dateRangeValue(value: string | number | undefined) {
  return typeof value === "string" && parseIsoDate(value) ? value : undefined;
}

/** Shared bento custom date picker: native date input is kept hidden for form contract compatibility. */
export function BentoDateInput({ wrapClassName = "", className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { wrapClassName?: string }) {
  const {
    id,
    value,
    defaultValue,
    onChange,
    disabled,
    name,
    required,
    min,
    max,
    placeholder,
    ...inputProps
  } = props;
  const minIso = dateRangeValue(min);
  const maxIso = dateRangeValue(max);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => String(defaultValue ?? ""));
  const currentValue = String(isControlled ? value ?? "" : internalValue);
  const selectedDate = parseIsoDate(currentValue);
  const today = new Date();

  // Draft: what the user is typing in the visible text field
  const [draft, setDraft] = useState(() => formatDateDisplay(currentValue));
  const [focused, setFocused] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  // textRef: visible editable input (replaces old button trigger)
  const textRef = useRef<HTMLInputElement>(null);
  const iconBtnRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const panelId = `${id ?? generatedId}-calendar`;
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selectedDate ?? today));
  const [activeDate, setActiveDate] = useState(() => dateToIso(selectedDate ?? today));
  const [panelPosition, setPanelPosition] = useState({ left: 0, top: 0, width: 308 });
  const days = useMemo(() => calendarCells(viewMonth), [viewMonth]);

  // Sync draft when external value changes and field is NOT focused
  useEffect(() => {
    if (!focused) {
      setDraft(formatDateDisplay(currentValue));
    }
    // Only react to currentValue changes; `focused` used as gate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (!open) return;
    const active = selectedDate ?? today;
    setViewMonth(startOfMonth(active));
    setActiveDate(dateToIso(active));
    // Intentionally only run when opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !textRef.current) return undefined;
    const positionPanel = () => {
      const rect = textRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 8;
      const panelWidth = Math.min(Math.max(rect.width, 308), Math.max(280, window.innerWidth - 16));
      const panelHeight = 352;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      setPanelPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - panelWidth - 8)),
        top: openUp ? Math.max(8, rect.top - panelHeight - gap) : Math.min(rect.bottom + gap, window.innerHeight - panelHeight - 8),
        width: panelWidth,
      });
    };
    positionPanel();
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open]);

  function withinRange(iso: string) {
    if (minIso && iso < minIso) return false;
    if (maxIso && iso > maxIso) return false;
    return true;
  }

  function emitChange(nextValue: string) {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (nativeRef.current) {
      nativeRef.current.value = nextValue;
      onChange?.({
        target: nativeRef.current,
        currentTarget: nativeRef.current,
      } as ChangeEvent<HTMLInputElement>);
    }
  }

  function choose(nextValue: string) {
    if (disabled || (nextValue && !withinRange(nextValue))) return;
    emitChange(nextValue);
    setDraft(formatDateDisplay(nextValue));
    setOpen(false);
    textRef.current?.focus();
  }

  function commitDraft(raw: string) {
    if (!raw.trim()) {
      emitChange("");
      setDraft("");
      return;
    }
    const iso = parseTypedDate(raw);
    if (iso && withinRange(iso)) {
      emitChange(iso);
      setDraft(formatDateDisplay(iso));
    } else {
      // Invalid or out of range → revert to last valid display
      setDraft(formatDateDisplay(currentValue));
    }
  }

  function setActiveFromDate(date: Date) {
    const iso = dateToIso(date);
    setActiveDate(iso);
    setViewMonth(startOfMonth(date));
    setOpen(true);
  }

  function handleTextKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft(draft);
      setOpen(false);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(formatDateDisplay(currentValue));
      setOpen(false);
      return;
    }
    // Arrow navigation works in the popup when open; in text field we let normal cursor move
    if (!open) return;
    const baseDate = parseIsoDate(activeDate) ?? selectedDate ?? today;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveFromDate(addDays(baseDate, 7));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveFromDate(addDays(baseDate, -7));
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setActiveFromDate(addDays(baseDate, -1));
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setActiveFromDate(addDays(baseDate, 1));
      return;
    }
    if (event.key === "PageUp") {
      event.preventDefault();
      setActiveFromDate(addMonths(baseDate, -1));
      return;
    }
    if (event.key === "PageDown") {
      event.preventDefault();
      setActiveFromDate(addMonths(baseDate, 1));
    }
  }

  function handleNativeChange(event: ChangeEvent<HTMLInputElement>) {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  }

  const showClear = currentValue && !required && !disabled;

  return (
    <div ref={rootRef} className={`group relative ${wrapClassName}`}>
      {/* Hidden native input — form contract (callers receive onChange with target.value = "YYYY-MM-DD") */}
      <input
        {...inputProps}
        ref={nativeRef}
        id={`${id ?? generatedId}-native`}
        name={name}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        type="date"
        value={currentValue}
        onChange={handleNativeChange}
        tabIndex={-1}
        className="sr-only"
      />

      {/* Visible row: text input + icon toggle */}
      <div
        className={`flex h-11 w-full items-center rounded-xl border border-ink-200 bg-white/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] transition duration-150 focus-within:border-brand-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-brand-100/70 hover:border-ink-300 hover:bg-white ${disabled ? "cursor-not-allowed opacity-50" : ""} ${className}`}
      >
        <input
          id={id}
          ref={textRef}
          type="text"
          inputMode="numeric"
          disabled={disabled}
          aria-label={placeholder || "Ngày (dd/mm/yyyy)"}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          placeholder={placeholder || "dd/mm/yyyy"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => {
            setFocused(true);
            textRef.current?.select();
          }}
          onBlur={() => {
            setFocused(false);
            commitDraft(draft);
          }}
          onKeyDown={handleTextKeyDown}
          className={`min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-[13px] font-semibold outline-none placeholder:text-ink-400 ${currentValue ? "text-ink-900" : "text-ink-400"}`}
        />

        {/* Clear button */}
        {showClear ? (
          <button
            type="button"
            aria-label="Xoá ngày"
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              choose("");
            }}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-400 transition hover:bg-rose-50 hover:text-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}

        {/* Calendar icon — popup toggle */}
        <button
          type="button"
          ref={iconBtnRef}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Mở lịch chọn ngày"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((prev) => !prev);
            textRef.current?.focus();
          }}
          className="mr-1.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-ink-500 shadow-sm ring-1 ring-ink-200 transition hover:text-brand-600 group-focus-within:bg-brand-50 group-focus-within:text-brand-600 group-focus-within:ring-brand-100"
        >
          <CalendarDays className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Chọn ngày"
          style={{
            left: panelPosition.left,
            top: panelPosition.top,
            width: panelPosition.width,
          }}
          className="fixed z-[90] rounded-2xl border border-ink-200 bg-white p-3 shadow-[0_24px_70px_rgba(31,29,43,0.2)] ring-1 ring-white/70"
          onMouseDown={(event) => event.preventDefault()}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setViewMonth((prev) => addMonths(prev, -1))}
              className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-brand-600"
              aria-label="Tháng trước"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="font-display text-[14px] font-bold text-ink-900">{monthLabel(viewMonth)}</p>
            <button
              type="button"
              onClick={() => setViewMonth((prev) => addMonths(prev, 1))}
              className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-brand-600"
              aria-label="Tháng sau"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {DATE_WEEKDAYS.map((day) => (
              <span key={day} className="py-1 text-[11px] font-bold text-ink-400">{day}</span>
            ))}
            {days.map((day) => {
              const iso = dateToIso(day);
              const isCurrentMonth = day.getMonth() === viewMonth.getMonth();
              const isSelected = currentValue === iso;
              const isToday = sameDay(day, today);
              const isActive = activeDate === iso;
              const isDisabled = !withinRange(iso);
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={isDisabled}
                  aria-label={day.toLocaleDateString("vi-VN", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}
                  aria-pressed={isSelected}
                  onMouseEnter={() => setActiveDate(iso)}
                  onClick={() => choose(iso)}
                  className={`grid h-8 place-items-center rounded-lg text-[12px] font-bold transition ${
                    isSelected
                      ? "bg-brand-500 text-white shadow-sm"
                      : isActive
                        ? "bg-brand-50 text-brand-700"
                        : isCurrentMonth
                          ? "text-ink-700 hover:bg-ink-100"
                          : "text-ink-300 hover:bg-ink-50"
                  } ${isToday && !isSelected ? "ring-1 ring-brand-200" : ""} disabled:cursor-not-allowed disabled:text-ink-200 disabled:hover:bg-transparent`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3">
            <button
              type="button"
              onClick={() => {
                const iso = dateToIso(today);
                setActiveDate(iso);
                setViewMonth(startOfMonth(today));
                choose(iso);
              }}
              disabled={!withinRange(dateToIso(today))}
              className="rounded-lg bg-brand-50 px-3 py-1.5 text-[12px] font-bold text-brand-600 transition hover:bg-brand-100 disabled:opacity-40"
            >
              Hôm nay
            </button>
            {!required && (
              <button
                type="button"
                onClick={() => choose("")}
                className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-ink-500 transition hover:bg-ink-100 hover:text-ink-800"
              >
                Xoá
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
